import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Col, Container, Row } from 'react-bootstrap';

import AppLayout from '../../../layouts/AppLayout/AppLayout';
import { useLanguage } from '../../../context/LanguageProvider/LanguageProvider';
import { EXPERTISE_ITEMS, enrichExpertiseItem, getExpertiseBySlug } from '../../../data/expertises';
import { NioSection, NioButton, NioIcon } from '../../../components';
import ExpertiseImage from '../../../components/ExpertiseImage/ExpertiseImage';
import ExpertiseTechTags from '../../../components/ExpertiseTechTags/ExpertiseTechTags';

function Expertises() {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!location.hash) return;
    const slug = location.hash.replace('#', '');
    if (getExpertiseBySlug(slug)) {
      navigate(`/expertises/${slug}`, { replace: true });
    }
  }, [location.hash, navigate]);

  return (
    <AppLayout variant={2} titleKey="pages.expertises.title" rootClass="layout-1">
      <div className="jassp-expertises-page">
      <section className="jassp-expertises-hero pt-7 pt-lg-120 pb-6 pb-lg-7">
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={11} xl={10}>
              <h1 className="jassp-expertises-hero-title display-4 fw-bold mb-4">{t('expertises.hero.h1')}</h1>
              <p className="jassp-expertises-hero-lead fs-18 mb-0 mx-auto">{t('expertises.hero.lead')}</p>
            </Col>
          </Row>
          <div className="jassp-expertises-icon-grid mt-5 mt-lg-6">
            {EXPERTISE_ITEMS.map((item) => (
              <Link key={item.slug} to={`/expertises/${item.slug}`} className="jassp-expertises-icon-card">
                <span className="jassp-expertises-icon-wrap" aria-hidden="true">
                  <NioIcon name={item.icon} />
                </span>
                <span className="jassp-expertises-icon-label">{t(item.titleKey)}</span>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      {EXPERTISE_ITEMS.map((item, idx) => (
        <section
          key={item.slug}
          id={item.slug}
          className={`jassp-expertise-block py-6 py-lg-7 scroll-margin-top ${
            idx % 2 === 1 ? 'jassp-expertise-block--alt' : ''
          }`}
        >
          <Container>
            <Row className="align-items-center gy-5">
              <Col lg={6} className={idx % 2 === 1 ? 'order-lg-2' : ''}>
                <h2 className="h2 fw-bold mb-3">{t(item.titleKey)}</h2>
                <p className="fs-16 text-gray-700 mb-3">{t(item.homeBodyKey)}</p>
                <ExpertiseTechTags
                  keywordKeys={enrichExpertiseItem(item).techKeywordsKeys}
                  className="mb-4"
                />
                <Link to={`/expertises/${item.slug}`} className="btn btn-outline-primary">
                  {t('expertises.detail.readMore')}
                  <NioIcon name="arrow-right" className="ms-2" />
                </Link>
              </Col>
              <Col lg={6} className={idx % 2 === 1 ? 'order-lg-1' : ''}>
                <div className="jassp-expertise-detail-media text-center text-lg-start">
                  <ExpertiseImage item={item} alt={t(item.altKey)} />
                </div>
              </Col>
            </Row>
          </Container>
        </section>
      ))}

      <NioSection className="jassp-expertises-methodology py-7 py-lg-120 bg-light">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9} xl={8} className="text-center">
              <h2 className="h2 fw-bold mb-4">{t('expertises.methodology.h2')}</h2>
              <p className="fs-18 text-gray-700 mb-5">{t('expertises.methodology.lead')}</p>
              <NioButton as="link" to="/contact-us" label={t('expertises.methodology.cta')} className="btn-primary">
                <NioIcon name="arrow-right" className="ms-2" />
              </NioButton>
            </Col>
          </Row>
        </Container>
      </NioSection>

      <section className="pb-7 pb-lg-120">
        <Container>
          <div className="text-center">
            <Link to="/realisations" className="btn btn-outline-primary me-2 mb-2">
              {t('expertises.linkPortfolio')}
            </Link>
            <Link to="/" className="btn btn-outline-secondary mb-2">
              <NioIcon name="arrow-left" className="me-2" />
              {t('portfolio.backHome')}
            </Link>
          </div>
        </Container>
      </section>
      </div>
    </AppLayout>
  );
}

export default Expertises;
